import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { partnerConferencesAPI, sessionsAPI, trainersAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { ContentItemLinkDialog } from '../components/content-items/ContentItemLinkDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft, Edit2, Trash2, Plus, Check, X, Upload, Copy, Loader2,
  MapPin, Calendar, Globe, Star, Users, ChevronDown, ChevronUp,
  Linkedin, RefreshCw, Image, CheckCircle2, Circle, ExternalLink, Clock, Train, Layers,
} from 'lucide-react';
import { differenceInDays, parseISO, isValid } from 'date-fns';
import { toast } from 'sonner';
import { format } from 'date-fns';

// ── Constants ─────────────────────────────────────────────────────────────────
const STATUS_VARIANTS = {
  requested: 'bg-yellow-100 text-yellow-800',
  planning: 'bg-slate-100 text-slate-800',
  scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-violet-100 text-violet-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};
const STATUS_LABELS = {
  requested: 'Requested', planning: 'Planning', scheduled: 'Scheduled',
  in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled',
};
const CURRENCIES = ['USD', 'EUR', 'INR'];
const CURRENCY_RATES = { USD: 1, EUR: 1.08, INR: 0.012 };
const ITINERARY_STATUSES = ['Booked', 'Local', 'By Train', 'By Car', 'TBD'];

function toUSD(amount, currency) {
  if (!amount) return 0;
  return Math.round((parseFloat(amount) * (CURRENCY_RATES[currency] || 1)) * 100) / 100;
}

// ── Checklist row ─────────────────────────────────────────────────────────────
function CheckItem({ label, checked, onToggle, extra }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-0">
      <button onClick={onToggle} className="mt-0.5 flex-shrink-0">
        {checked
          ? <CheckCircle2 className="w-5 h-5 text-lime" />
          : <Circle className="w-5 h-5 text-muted-foreground/40" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${checked ? 'line-through text-muted-foreground' : ''}`}>{label}</p>
        {extra}
      </div>
    </div>
  );
}

// ── Trainer slot row ──────────────────────────────────────────────────────────
function TrainerRow({ slot, onChange, onRemove }) {
  const hotelUSD = toUSD(slot.hotel_amount, slot.currency);
  const flightUSD = toUSD(slot.flight_amount, slot.currency);
  const totalUSD = hotelUSD + flightUSD;

  return (
    <TableRow>
      <TableCell>
        <Input
          value={slot.trainer_name || ''}
          onChange={e => onChange('trainer_name', e.target.value)}
          placeholder="Trainer name"
          className="h-8 text-sm min-w-[120px]"
        />
      </TableCell>
      <TableCell>
        <Input
          value={slot.trainer_email || ''}
          onChange={e => onChange('trainer_email', e.target.value)}
          placeholder="email@domain.com"
          className="h-8 text-sm min-w-[140px]"
        />
      </TableCell>
      <TableCell>
        <Input
          value={slot.contract_no || ''}
          onChange={e => onChange('contract_no', e.target.value)}
          placeholder="S-111"
          className="h-8 text-sm w-24"
        />
      </TableCell>
      <TableCell className="text-center">
        <Checkbox
          checked={slot.te_shared || false}
          onCheckedChange={v => onChange('te_shared', v)}
        />
      </TableCell>
      <TableCell className="text-center">
        <Checkbox
          checked={slot.dry_run_reached || false}
          onCheckedChange={v => onChange('dry_run_reached', v)}
        />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1 min-w-[160px]">
          <Input
            type="number"
            value={slot.hotel_amount || ''}
            onChange={e => onChange('hotel_amount', e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="Hotel"
            className="h-8 text-sm w-20"
          />
          <span className="text-muted-foreground text-xs">/</span>
          <Input
            type="number"
            value={slot.flight_amount || ''}
            onChange={e => onChange('flight_amount', e.target.value ? parseFloat(e.target.value) : null)}
            placeholder="Flight"
            className="h-8 text-sm w-20"
          />
          <Select value={slot.currency || 'USD'} onValueChange={v => onChange('currency', v)}>
            <SelectTrigger className="h-8 w-16 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </TableCell>
      <TableCell className="text-sm font-medium text-right">
        {totalUSD > 0 ? `$${totalUSD.toFixed(2)}` : '—'}
      </TableCell>
      <TableCell>
        <Select value={slot.itinerary_status || ''} onValueChange={v => onChange('itinerary_status', v)}>
          <SelectTrigger className="h-8 text-xs w-24">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            {ITINERARY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Button variant="ghost" size="icon" onClick={onRemove} className="h-7 w-7 text-destructive hover:text-destructive">
          <X className="w-3.5 h-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

// ── Track card ────────────────────────────────────────────────────────────────
function TrackCard({ track, onSave, onDelete, onLinkContentItem }) {
  const navigate = useNavigate();
  const [slots, setSlots] = useState(track.conference_trainers || []);
  const [title, setTitle] = useState(track.title || '');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contentItemDialogOpen, setContentItemDialogOpen] = useState(false);

  const addSlot = () => {
    setSlots(prev => [...prev, {
      slot_id: `slot-${Date.now()}`,
      trainer_name: '', trainer_email: '', contract_no: '',
      te_shared: false, dry_run_reached: false,
      hotel_amount: null, flight_amount: null, currency: 'USD',
      hotel_usd: null, flight_usd: null,
      travel_remarks: '', itinerary_status: '',
    }]);
    setEditing(true);
  };

  const updateSlot = (idx, field, value) => {
    setSlots(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      // Recalculate USD values
      const s = next[idx];
      next[idx].hotel_usd = toUSD(s.hotel_amount, s.currency);
      next[idx].flight_usd = toUSD(s.flight_amount, s.currency);
      return next;
    });
    setEditing(true);
  };

  const removeSlot = (idx) => {
    setSlots(prev => prev.filter((_, i) => i !== idx));
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(track.id, title, slots);
      setEditing(false);
      toast.success('Track saved');
    } catch {
      toast.error('Failed to save track');
    } finally {
      setSaving(false);
    }
  };

  const totalUSD = slots.reduce((sum, s) => sum + toUSD(s.hotel_amount, s.currency) + toUSD(s.flight_amount, s.currency), 0);

  return (
    <Card className="border border-border/60">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            value={title}
            onChange={e => { setTitle(e.target.value); setEditing(true); }}
            className="font-semibold text-sm h-8 w-36 sm:w-48 flex-shrink-0"
            placeholder="Track title"
          />
          {track.content_item_id ? (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button type="button" onClick={() => navigate(`/content-items/${track.content_item_id}`)}
                className="text-xs text-lime-700 hover:underline flex items-center gap-1" data-testid={`view-linked-content-item-btn-${track.id}`}>
                <Layers className="w-3.5 h-3.5" />{track.content_item_title || 'View Content Dev Course'}
              </button>
              <button type="button" onClick={() => onLinkContentItem(track.id, null)} className="text-muted-foreground hover:text-red-600" title="Unlink Content Dev course" data-testid={`unlink-content-item-btn-${track.id}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setContentItemDialogOpen(true)} className="h-8 text-muted-foreground hover:text-foreground flex-shrink-0" data-testid={`link-content-item-btn-${track.id}`}>
              <Layers className="w-3.5 h-3.5 mr-1" />Link Content Dev Course
            </Button>
          )}
          <div className="flex gap-2 flex-shrink-0 ml-auto">
            {editing && (
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90 h-8">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                <span className="ml-1">Save</span>
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={addSlot} className="h-8 text-muted-foreground hover:text-foreground">
              <Plus className="w-3.5 h-3.5 mr-1" />Add Trainer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(track.id)} className="h-8 text-destructive hover:text-destructive">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
        {slots.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {slots.length} trainer{slots.length !== 1 ? 's' : ''} · Total T&E: ${totalUSD.toFixed(2)} USD
          </p>
        )}
        <ContentItemLinkDialog open={contentItemDialogOpen} onOpenChange={setContentItemDialogOpen} onSelect={(item) => { setContentItemDialogOpen(false); onLinkContentItem(track.id, item); }} />
      </CardHeader>
      {slots.length > 0 && (
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Email</TableHead>
                  <TableHead className="text-xs">Contract #</TableHead>
                  <TableHead className="text-xs text-center">T&E Shared</TableHead>
                  <TableHead className="text-xs text-center">Dry Run</TableHead>
                  <TableHead className="text-xs">Hotel / Flight (Amount + Currency)</TableHead>
                  <TableHead className="text-xs text-right">Total USD</TableHead>
                  <TableHead className="text-xs">Itinerary</TableHead>
                  <TableHead className="w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slots.map((slot, idx) => (
                  <TrainerRow
                    key={slot.slot_id || idx}
                    slot={slot}
                    onChange={(field, value) => updateSlot(idx, field, value)}
                    onRemove={() => removeSlot(idx)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export const PartnerConferenceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlight, setHighlight] = useState(!!location.state?.highlight);
  useEffect(() => {
    if (!highlight) return;
    const t = setTimeout(() => setHighlight(false), 2500);
    return () => clearTimeout(t);
  }, [highlight]);
  const { user } = useAuth();

  const [conf, setConf] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  // LinkedIn post
  const [linkedinPost, setLinkedinPost] = useState('');
  const [generatingPost, setGeneratingPost] = useState(false);
  const [postCopied, setPostCopied] = useState(false);

  // Travel tab
  const [travelEditing, setTravelEditing] = useState(null); // slot_id being edited
  const [travelSaving, setTravelSaving] = useState(false);

  // Add track dialog
  const [addTrackOpen, setAddTrackOpen] = useState(false);
  const [newTrackTitle, setNewTrackTitle] = useState('');
  const [addingTrack, setAddingTrack] = useState(false);

  // Outcomes edit
  const [outcomesEdit, setOutcomesEdit] = useState(false);
  const [outcomes, setOutcomes] = useState({ total_attendance: '', conference_feedback_score: '', trainer_feedback_summary: '' });
  const [savingOutcomes, setSavingOutcomes] = useState(false);

  // Content date input
  const [contentDateEdit, setContentDateEdit] = useState('');

  const fileInputRef = useRef();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [confData, tracksData, photosData] = await Promise.all([
        partnerConferencesAPI.getById(id),
        sessionsAPI.getByProject(id),
        partnerConferencesAPI.getPhotos(id),
      ]);
      setConf(confData);
      setTracks(tracksData);
      setPhotos(photosData);
      setOutcomes({
        total_attendance: confData.total_attendance ?? '',
        conference_feedback_score: confData.conference_feedback_score ?? '',
        trainer_feedback_summary: confData.trainer_feedback_summary ?? '',
      });
      setContentDateEdit(confData.content_shared_date || '');
    } catch {
      toast.error('Failed to load conference');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // ── Checklist toggle ──────────────────────────────────────────────────────
  const toggleChecklist = async (field) => {
    const newVal = !conf[field];
    setConf(prev => ({ ...prev, [field]: newVal }));
    try {
      await partnerConferencesAPI.update(id, { [field]: newVal });
    } catch {
      setConf(prev => ({ ...prev, [field]: !newVal }));
      toast.error('Failed to update checklist');
    }
  };

  const saveContentDate = async () => {
    try {
      await partnerConferencesAPI.update(id, { content_shared_date: contentDateEdit });
      setConf(prev => ({ ...prev, content_shared_date: contentDateEdit }));
      toast.success('Content date saved');
    } catch {
      toast.error('Failed to save');
    }
  };

  const saveTextField = async (field, value) => {
    try {
      await partnerConferencesAPI.update(id, { [field]: value });
      setConf(prev => ({ ...prev, [field]: value }));
    } catch {
      toast.error('Failed to save');
    }
  };

  // ── Tracks ────────────────────────────────────────────────────────────────
  const handleAddTrack = async () => {
    if (!newTrackTitle.trim()) { toast.error('Track title required'); return; }
    setAddingTrack(true);
    try {
      const payload = {
        training_project_id: id,
        title: newTrackTitle.trim(),
        session_date: conf.preferred_dates || new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_time: '17:00',
        session_type: 'in_person',
        conference_trainers: [],
      };
      await sessionsAPI.create(payload);
      setAddTrackOpen(false);
      setNewTrackTitle('');
      toast.success('Track added');
      load();
    } catch {
      toast.error('Failed to add track');
    } finally {
      setAddingTrack(false);
    }
  };

  const handleSaveTrack = async (trackId, title, slots) => {
    await sessionsAPI.update(trackId, { title, conference_trainers: slots });
    load();
  };

  const handleDeleteTrack = async (trackId) => {
    if (!window.confirm('Remove this track?')) return;
    try {
      await sessionsAPI.delete(trackId);
      toast.success('Track removed');
      load();
    } catch {
      toast.error('Failed to remove track');
    }
  };

  const handleLinkTrackContentItem = async (trackId, item) => {
    try {
      await sessionsAPI.update(trackId, { content_item_id: item?.id || null, content_item_title: item?.title || null });
      toast.success(item ? 'Linked to Content Dev course' : 'Content Dev link removed');
      load();
    } catch {
      toast.error('Failed to update Content Dev link');
    }
  };

  // ── Outcomes ──────────────────────────────────────────────────────────────
  const saveOutcomes = async () => {
    setSavingOutcomes(true);
    try {
      const payload = {
        total_attendance: outcomes.total_attendance !== '' ? parseInt(outcomes.total_attendance) : null,
        conference_feedback_score: outcomes.conference_feedback_score !== '' ? parseFloat(outcomes.conference_feedback_score) : null,
        trainer_feedback_summary: outcomes.trainer_feedback_summary || null,
      };
      await partnerConferencesAPI.update(id, payload);
      setConf(prev => ({ ...prev, ...payload }));
      setOutcomesEdit(false);
      toast.success('Outcomes saved');
    } catch {
      toast.error('Failed to save outcomes');
    } finally {
      setSavingOutcomes(false);
    }
  };

  // ── Photos ────────────────────────────────────────────────────────────────
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    for (const file of files) {
      try {
        await partnerConferencesAPI.uploadPhoto(id, file);
        toast.success(`Uploaded ${file.name}`);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    load();
    e.target.value = '';
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await partnerConferencesAPI.deletePhoto(photoId);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove photo');
    }
  };

  // ── LinkedIn post ─────────────────────────────────────────────────────────
  const generatePost = async () => {
    setGeneratingPost(true);
    setLinkedinPost('');
    const token = localStorage.getItem('token');
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
      const response = await fetch(`${base}/api/partner-conferences/${id}/generate-linkedin-post`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Generation failed');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let text = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ') && line !== 'data: [DONE]') {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                text += data.text;
                setLinkedinPost(text);
              }
            } catch { /* ignore parse errors */ }
          }
        }
      }
    } catch {
      toast.error('Failed to generate LinkedIn post');
    } finally {
      setGeneratingPost(false);
    }
  };

  const copyPost = () => {
    navigator.clipboard.writeText(linkedinPost);
    setPostCopied(true);
    setTimeout(() => setPostCopied(false), 2000);
    toast.success('Copied to clipboard!');
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(new Date(d), 'MMM d, yyyy'); } catch { return d; }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-lime" />
      </div>
    );
  }

  if (!conf) {
    return <div className="text-center py-16 text-muted-foreground">Conference not found</div>;
  }

  const checklist = [
    { field: 'content_labs_shared', label: 'Content + Labs shared with trainers', extra: conf.content_labs_shared ? (
      <div className="flex items-center gap-2 mt-1">
        <Input
          type="date"
          value={contentDateEdit}
          onChange={e => setContentDateEdit(e.target.value)}
          className="h-7 text-xs w-36"
        />
        <Button size="sm" variant="ghost" onClick={saveContentDate} className="h-7 px-2 text-xs">Save date</Button>
        {conf.content_shared_date && <span className="text-xs text-muted-foreground">on {formatDate(conf.content_shared_date)}</span>}
      </div>
    ) : null },
    { field: 'internal_sync_done', label: 'Internal sync-up completed' },
    { field: 'sales_table_read_done', label: 'Sales Table Read with MS done' },
    { field: 'technical_table_read_done', label: 'Technical Table Read with MS done' },
    { field: 'id_card_lanyard_shared', label: 'ID card + lanyard shared with trainers' },
  ];

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl" data-testid="conference-detail-page">
      {/* Header */}
      <div className={`flex flex-col sm:flex-row sm:items-start gap-4 rounded-lg -m-2 p-2 transition-colors duration-1000 ${highlight ? 'bg-lime/20' : ''}`} data-testid="conference-detail-header">
        <Button variant="ghost" size="icon" onClick={() => navigate('/partner-conferences')} className="self-start">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge className={STATUS_VARIANTS[conf.status] || ''}>
              {STATUS_LABELS[conf.status] || conf.status}
            </Badge>
            {conf.region && <Badge variant="outline">{conf.region}</Badge>}
            {conf.delivery_language && conf.delivery_language !== 'English' && (
              <Badge variant="outline">{conf.delivery_language}</Badge>
            )}
          </div>
          <h1 className="text-2xl font-heading font-bold break-words">{conf.title}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
            {(conf.city || conf.country) && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {[conf.city, conf.country].filter(Boolean).join(', ')}
              </span>
            )}
            {conf.preferred_dates && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(conf.preferred_dates)}
              </span>
            )}
            {conf.po_number && <span className="font-mono text-xs">PO: {conf.po_number}</span>}
            {conf.workload && <span>{conf.workload}</span>}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => navigate(`/partner-conferences/${id}/edit`)}>
            <Edit2 className="w-4 h-4 mr-1.5" />Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive border-destructive/30 hover:bg-destructive/10"
            onClick={async () => {
              if (!window.confirm('Delete this conference?')) return;
              await partnerConferencesAPI.delete(id);
              navigate('/partner-conferences');
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Total Attendance', value: conf.total_attendance ? conf.total_attendance.toLocaleString() : '—', icon: Users },
          { label: 'Feedback Score', value: conf.conference_feedback_score ? `${conf.conference_feedback_score}/5` : '—', icon: Star },
          { label: 'Tracks', value: tracks.length.toString(), icon: Globe },
          { label: 'Trainers', value: tracks.reduce((s, t) => s + (t.conference_trainers?.length || 0), 0).toString(), icon: Users },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label} className="border border-border/50">
            <CardContent className="py-4 px-5">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground font-medium">{label}</p>
                <Icon className="w-4 h-4 text-muted-foreground/50" />
              </div>
              <p className="text-2xl font-bold mt-1">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: checklist + outcomes */}
        <div className="space-y-6">
          {/* Coordination Checklist */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Coordination Checklist</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {checklist.map(({ field, label, extra }) => (
                <CheckItem
                  key={field}
                  label={label}
                  checked={!!conf[field]}
                  onToggle={() => toggleChecklist(field)}
                  extra={extra}
                />
              ))}

              {/* Lab Key + Skillable fields */}
              <div className="pt-3 space-y-3">
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Lab Key</Label>
                  <Input
                    value={conf.lab_key || ''}
                    onChange={e => setConf(p => ({ ...p, lab_key: e.target.value }))}
                    onBlur={e => saveTextField('lab_key', e.target.value)}
                    placeholder="Lab access key..."
                    className="h-8 text-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Skillable Lab Support Link</Label>
                  <Input
                    value={conf.skillable_link || ''}
                    onChange={e => setConf(p => ({ ...p, skillable_link: e.target.value }))}
                    onBlur={e => saveTextField('skillable_link', e.target.value)}
                    placeholder="https://..."
                    className="h-8 text-sm mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Skillable Support Contact</Label>
                  <Input
                    value={conf.skillable_contact || ''}
                    onChange={e => setConf(p => ({ ...p, skillable_contact: e.target.value }))}
                    onBlur={e => saveTextField('skillable_contact', e.target.value)}
                    placeholder="Name / email..."
                    className="h-8 text-sm mt-1"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Outcomes */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Event Outcomes</CardTitle>
                {!outcomesEdit && (
                  <Button variant="ghost" size="sm" onClick={() => setOutcomesEdit(true)} className="h-7 text-xs" data-testid="edit-outcomes-btn">
                    <Edit2 className="w-3 h-3 mr-1" />Edit
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              {outcomesEdit ? (
                <>
                  <div>
                    <Label className="text-xs">Total Attendance</Label>
                    <Input
                      type="number"
                      value={outcomes.total_attendance}
                      onChange={e => setOutcomes(p => ({ ...p, total_attendance: e.target.value }))}
                      className="h-8 text-sm mt-1"
                      placeholder="e.g. 650"
                      data-testid="outcomes-attendance"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Feedback Score (out of 5)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      value={outcomes.conference_feedback_score}
                      onChange={e => setOutcomes(p => ({ ...p, conference_feedback_score: e.target.value }))}
                      className="h-8 text-sm mt-1"
                      placeholder="e.g. 4.2"
                      data-testid="outcomes-score"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Trainer Feedback Summary</Label>
                    <Textarea
                      value={outcomes.trainer_feedback_summary}
                      onChange={e => setOutcomes(p => ({ ...p, trainer_feedback_summary: e.target.value }))}
                      rows={3}
                      className="text-sm mt-1"
                      placeholder="Summary of trainer feedback..."
                      data-testid="outcomes-feedback"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={saveOutcomes} disabled={savingOutcomes} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-outcomes-btn">
                      {savingOutcomes ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setOutcomesEdit(false)}>Cancel</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center py-1.5 border-b">
                    <span className="text-sm text-muted-foreground">Total Attendance</span>
                    <span className="text-sm font-semibold">{conf.total_attendance ? conf.total_attendance.toLocaleString() : '—'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 border-b">
                    <span className="text-sm text-muted-foreground">Feedback Score</span>
                    <span className="text-sm font-semibold flex items-center gap-1">
                      {conf.conference_feedback_score != null
                        ? <><Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />{conf.conference_feedback_score}/5</>
                        : '—'}
                    </span>
                  </div>
                  {conf.trainer_feedback_summary && (
                    <p className="text-sm text-muted-foreground pt-1 whitespace-pre-wrap">{conf.trainer_feedback_summary}</p>
                  )}
                  {!conf.trainer_feedback_summary && !conf.conference_feedback_score && !conf.total_attendance && (
                    <p className="text-sm text-muted-foreground italic">No outcomes recorded yet</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right column: tracks, photos, linkedin */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tracks */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Technical Tracks</CardTitle>
                <Button
                  size="sm"
                  onClick={() => setAddTrackOpen(true)}
                  className="bg-lime text-lime-foreground hover:bg-lime/90 h-8"
                  data-testid="add-track-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Track
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-4">
              {tracks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Globe className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No tracks yet. Add your first technical track.</p>
                </div>
              ) : (
                tracks.map(track => (
                  <TrackCard
                    key={track.id}
                    track={track}
                    onSave={handleSaveTrack}
                    onDelete={handleDeleteTrack}
                    onLinkContentItem={handleLinkTrackContentItem}
                  />
                ))
              )}
            </CardContent>
          </Card>

          {/* Event Photos */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Event Photos</CardTitle>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="h-8"
                  data-testid="upload-photo-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1" />Upload Photos
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {photos.length === 0 ? (
                <div
                  className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-lime/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Image className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">Click to upload event photos</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">JPG, PNG, WebP supported</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {photos.map(photo => (
                    <div key={photo.id} className="relative group rounded-lg overflow-hidden aspect-video bg-muted">
                      <img
                        src={partnerConferencesAPI.getPhotoUrl(photo.id)}
                        alt={photo.original_filename}
                        className="w-full h-full object-cover"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-7 text-xs"
                          onClick={() => handleDeletePhoto(photo.id)}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />Remove
                        </Button>
                      </div>
                      <div className="absolute bottom-1 left-1 right-1">
                        <p className="text-white text-xs truncate drop-shadow">{photo.original_filename}</p>
                      </div>
                    </div>
                  ))}
                  <div
                    className="border-2 border-dashed border-border rounded-lg aspect-video flex items-center justify-center cursor-pointer hover:border-lime/50 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Plus className="w-6 h-6 text-muted-foreground/40" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* LinkedIn Post Generator */}
          <Card className="border border-blue-200/50 bg-blue-50/20">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Linkedin className="w-5 h-5 text-blue-600" />
                  <CardTitle className="text-base">LinkedIn Thank You Post</CardTitle>
                </div>
                <Button
                  size="sm"
                  onClick={generatePost}
                  disabled={generatingPost}
                  className="bg-blue-600 hover:bg-blue-700 text-white h-8"
                  data-testid="generate-linkedin-btn"
                >
                  {generatingPost
                    ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Generating…</>
                    : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Generate Post</>}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                AI-crafted post thanking Microsoft regional teams · includes attendance summary + hashtags
              </p>
            </CardHeader>
            {linkedinPost && (
              <CardContent className="pt-0">
                <div className="relative bg-white rounded-lg border p-4">
                  <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">{linkedinPost}</pre>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyPost}
                    className="mt-3 w-full"
                    data-testid="copy-linkedin-post-btn"
                  >
                    {postCopied
                      ? <><Check className="w-3.5 h-3.5 mr-1.5 text-green-600" />Copied!</>
                      : <><Copy className="w-3.5 h-3.5 mr-1.5" />Copy to Clipboard</>}
                  </Button>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>

      {/* Add Track Dialog */}
      <Dialog open={addTrackOpen} onOpenChange={setAddTrackOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Technical Track</DialogTitle>
            <DialogDescription>Enter the track name. You can add trainer slots after creation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input
              placeholder="e.g. Tech Excellence in Agentic AI"
              value={newTrackTitle}
              onChange={e => setNewTrackTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddTrack(); }}
              data-testid="new-track-title"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setAddTrackOpen(false)}>Cancel</Button>
              <Button
                onClick={handleAddTrack}
                disabled={addingTrack}
                className="bg-lime text-lime-foreground hover:bg-lime/90"
                data-testid="confirm-add-track-btn"
              >
                {addingTrack ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Track'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Travel Tracker */}
      <Card className="border border-border/60" data-testid="conf-travel-tracker">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Train className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base">Travel Tracker</CardTitle>
            {conf.preferred_dates && (() => {
              try {
                const d = differenceInDays(parseISO(conf.preferred_dates), new Date());
                if (d < 0) return <span className="text-xs text-muted-foreground ml-2">Event passed</span>;
                const color = d < 7 ? 'text-red-600 bg-red-50' : d < 21 ? 'text-yellow-700 bg-yellow-50' : 'text-green-700 bg-green-50';
                return (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ml-2 ${color}`}>
                    <Clock className="w-3 h-3" />{d} days to event
                  </span>
                );
              } catch { return null; }
            })()}
          </div>
          <p className="text-xs text-muted-foreground">Consolidated travel overview for all conference trainers</p>
        </CardHeader>
        <CardContent className="pt-0">
          {tracks.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-4">No tracks yet — add tracks to see trainer travel details</p>
          ) : (
            <div className="space-y-4">
              {tracks.map(track => {
                const slots = track.conference_trainers || [];
                if (slots.length === 0) return null;
                return (
                  <div key={track.id}>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{track.title}</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-xs text-muted-foreground">
                            <th className="text-left py-1.5 pr-4 font-medium">Trainer</th>
                            <th className="text-left py-1.5 pr-4 font-medium">Internal Call</th>
                            <th className="text-left py-1.5 pr-4 font-medium">Flight / Train Details</th>
                            <th className="text-left py-1.5 pr-4 font-medium">Itinerary</th>
                            <th className="text-right py-1.5 font-medium">T&E (USD)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slots.map(slot => {
                            const totalUSD = (slot.hotel_usd || 0) + (slot.flight_usd || 0);
                            return (
                              <tr key={slot.slot_id} className="border-b last:border-0 hover:bg-muted/30">
                                <td className="py-2 pr-4">
                                  <p className="font-medium">{slot.trainer_name || <span className="text-muted-foreground italic">TBD</span>}</p>
                                  {slot.trainer_email && <p className="text-xs text-muted-foreground">{slot.trainer_email}</p>}
                                </td>
                                <td className="py-2 pr-4">
                                  {slot.internal_call_done
                                    ? <CheckCircle2 className="w-4 h-4 text-lime" />
                                    : <Circle className="w-4 h-4 text-muted-foreground/40" />}
                                </td>
                                <td className="py-2 pr-4">
                                  <span className="text-sm">{slot.flight_details || <span className="text-muted-foreground italic text-xs">Not provided</span>}</span>
                                </td>
                                <td className="py-2 pr-4">
                                  {slot.itinerary_status
                                    ? <Badge variant="outline" className="text-xs">{slot.itinerary_status}</Badge>
                                    : <span className="text-muted-foreground text-xs">—</span>}
                                </td>
                                <td className="py-2 text-right font-medium">
                                  {totalUSD > 0 ? `$${totalUSD.toFixed(2)}` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PartnerConferenceDetailPage;
